import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { useTenant } from '../../context/TenantContext';
import {
  User,
  MapPin,
  Users,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Baby,
  Plus,
  Trash2,
} from 'lucide-react';

const ParentOnboarding = () => {
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'Cricket Academy';
  const navigate = useNavigate();
  const { _user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  // Personal details
  const [personalDetails, setPersonalDetails] = useState({
    name: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  
  // Address details
  const [addressDetails, setAddressDetails] = useState({
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  
  // Children
  const [children, setChildren] = useState([{
    full_name: '',
    date_of_birth: '',
    gender: '',
    skill_level: 'beginner'
  }]);

  // Check if onboarding is already complete
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const response = await api.get('/auth/me/profile');
        const profile = response.data;
        
        // Check if onboarding is complete
        if (profile.onboarding_completed) {
          navigate('/parent');
          return;
        }
        
        // Check if parent already has children linked
        try {
          const childrenRes = await api.get('/auth/me/children');
          if (childrenRes.data && childrenRes.data.length > 0) {
            // Parent already has children - skip to completion or just mark complete
            toast.info('Welcome back! Your account already has children linked.');
            // Mark onboarding as complete since they already have children
            await api.put('/parents/' + profile.id + '/complete-onboarding');
            await refreshUser();
            navigate('/parent');
            return;
          }
        } catch (childError) {
          // No children found, continue with onboarding
          console.log('No existing children found, proceeding with onboarding');
        }
        
        // Pre-fill existing data
        setPersonalDetails({
          name: profile.name || '',
          phone: profile.phone || '',
          emergency_contact_name: profile.emergency_contact_name || '',
          emergency_contact_phone: profile.emergency_contact_phone || ''
        });
        
        setAddressDetails({
          address: profile.address || '',
          city: profile.city || '',
          state: profile.state || '',
          zip: profile.zip || ''
        });
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkOnboardingStatus();
  }, [navigate, refreshUser]);

  const addChild = () => {
    setChildren([...children, {
      full_name: '',
      date_of_birth: '',
      gender: '',
      skill_level: 'beginner'
    }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    setChildren(updated);
  };

  const validateStep1 = () => {
    if (!personalDetails.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!personalDetails.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (!personalDetails.emergency_contact_name.trim()) {
      toast.error('Please enter emergency contact name');
      return false;
    }
    if (!personalDetails.emergency_contact_phone.trim()) {
      toast.error('Please enter emergency contact phone');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!addressDetails.address.trim()) {
      toast.error('Please enter your address');
      return false;
    }
    if (!addressDetails.city.trim()) {
      toast.error('Please enter your city');
      return false;
    }
    if (!addressDetails.state.trim()) {
      toast.error('Please enter your state');
      return false;
    }
    if (!addressDetails.zip.trim()) {
      toast.error('Please enter your ZIP code');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child.full_name.trim()) {
        toast.error(`Please enter name for child ${i + 1}`);
        return false;
      }
      if (!child.date_of_birth) {
        toast.error(`Please enter date of birth for ${child.full_name || `child ${i + 1}`}`);
        return false;
      }
      if (!child.gender) {
        toast.error(`Please select gender for ${child.full_name || `child ${i + 1}`}`);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    try {
      // Submit all data
      await api.post('/parent/complete-onboarding', {
        personal_details: personalDetails,
        address_details: addressDetails,
        children: children
      });
      
      toast.success('Welcome! Your profile has been set up successfully.');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      navigate('/parent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Welcome to {tenantName}</h1>
          <p className="text-slate-600 mt-2">Let's set up your account to get started</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                currentStep === step 
                  ? 'bg-emerald-600 text-white' 
                  : currentStep > step 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-slate-200 text-slate-500'
              }`}>
                {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
              </div>
              {step < 3 && (
                <div className={`w-12 h-1 mx-2 rounded ${
                  currentStep > step ? 'bg-emerald-400' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Labels */}
        <div className="flex justify-center gap-8 mb-8 text-sm">
          <span className={currentStep >= 1 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
            Personal Info
          </span>
          <span className={currentStep >= 2 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
            Address
          </span>
          <span className={currentStep >= 3 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
            Children
          </span>
        </div>

        {/* Step 1: Personal Details */}
        {currentStep === 1 && (
          <Card className="border-slate-200 shadow-lg" data-testid="onboarding-step-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-600" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Please provide your contact details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="Your full name"
                    value={personalDetails.name}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, name: e.target.value })}
                    data-testid="onboarding-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    placeholder="(xxx) xxx-xxxx"
                    value={personalDetails.phone}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, phone: e.target.value })}
                    data-testid="onboarding-phone"
                  />
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      placeholder="Emergency contact name"
                      value={personalDetails.emergency_contact_name}
                      onChange={(e) => setPersonalDetails({ ...personalDetails, emergency_contact_name: e.target.value })}
                      data-testid="onboarding-emergency-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone *</Label>
                    <Input
                      placeholder="(xxx) xxx-xxxx"
                      value={personalDetails.emergency_contact_phone}
                      onChange={(e) => setPersonalDetails({ ...personalDetails, emergency_contact_phone: e.target.value })}
                      data-testid="onboarding-emergency-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Address Details */}
        {currentStep === 2 && (
          <Card className="border-slate-200 shadow-lg" data-testid="onboarding-step-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-emerald-600" />
                Address Information
              </CardTitle>
              <CardDescription>
                Please provide your home address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Street Address *</Label>
                <Input
                  placeholder="123 Main Street"
                  value={addressDetails.address}
                  onChange={(e) => setAddressDetails({ ...addressDetails, address: e.target.value })}
                  data-testid="onboarding-address"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    placeholder="City"
                    value={addressDetails.city}
                    onChange={(e) => setAddressDetails({ ...addressDetails, city: e.target.value })}
                    data-testid="onboarding-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input
                    placeholder="State"
                    value={addressDetails.state}
                    onChange={(e) => setAddressDetails({ ...addressDetails, state: e.target.value })}
                    data-testid="onboarding-state"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code *</Label>
                  <Input
                    placeholder="ZIP"
                    value={addressDetails.zip}
                    onChange={(e) => setAddressDetails({ ...addressDetails, zip: e.target.value })}
                    data-testid="onboarding-zip"
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Children */}
        {currentStep === 3 && (
          <Card className="border-slate-200 shadow-lg" data-testid="onboarding-step-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-600" />
                Add Your Children
              </CardTitle>
              <CardDescription>
                Add at least one child to your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {children.map((child, index) => (
                <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="bg-white">
                      <Baby className="h-3 w-3 mr-1" />
                      Child {index + 1}
                    </Badge>
                    {children.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChild(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        placeholder="Child's full name"
                        value={child.full_name}
                        onChange={(e) => updateChild(index, 'full_name', e.target.value)}
                        data-testid={`onboarding-child-${index}-name`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      <Input
                        type="date"
                        value={child.date_of_birth}
                        onChange={(e) => updateChild(index, 'date_of_birth', e.target.value)}
                        data-testid={`onboarding-child-${index}-dob`}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Gender *</Label>
                      <Select
                        value={child.gender}
                        onValueChange={(value) => updateChild(index, 'gender', value)}
                      >
                        <SelectTrigger data-testid={`onboarding-child-${index}-gender`}>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Skill Level</Label>
                      <Select
                        value={child.skill_level}
                        onValueChange={(value) => updateChild(index, 'skill_level', value)}
                      >
                        <SelectTrigger data-testid={`onboarding-child-${index}-skill`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addChild}
                className="w-full border-dashed"
                data-testid="add-another-child"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Child
              </Button>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button 
                  onClick={handleComplete} 
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="complete-onboarding"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Setting up...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Complete Setup
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ParentOnboarding;
